import React from 'react';
import { ChevronLeft, ChevronRight, Ellipsis } from 'lucide-react';
import Button from './primitives/button';
import cn from './primitives/cn';
import { Select } from './primitives/input';

/** A skipped run of pages in `pageWindow`'s output. */
export const PAGE_GAP = 'gap';

const range = (from, to) => Array.from({ length: to - from + 1 }, (_, index) => from + index);

/**
 * Previous and Next: small secondary buttons, 32 tall with 13px labels and 14px chevrons,
 * that go flat grey at the end of the run. Disabled is the shared disabled look (fill, light
 * edge, subtle ink) rather than an opacity, because an opacity of a token is a new colour.
 */
const STEP = cn(
  'tw-px-2.5 tw-gap-1.5 tw-text-small tw-font-semibold tw-text-ink-2 tw-shadow-none',
  'hover:tw-bg-fill hover:tw-text-ink-2',
  'disabled:tw-opacity-100 disabled:tw-bg-fill disabled:tw-border-line-light',
  'disabled:tw-text-subtle disabled:tw-shadow-none'
);

/** A page number: a 32px ghost square, the figures tabular so the run does not jitter. */
const PAGE = 'tw-text-small tw-font-semibold tw-tabular-nums tw-shadow-none';
/** The page on screen: the selected fill, as on the rail, which does not change under the pointer. */
const CURRENT = cn(
  PAGE,
  'tw-bg-page-current tw-text-white hover:tw-bg-page-current hover:tw-text-white'
);

/** Row numbers read as figures with a thousands separator, as the rest of the page does. */
const figures = new Intl.NumberFormat('en-US');

/**
 * Which page numbers to draw: the first and the last, the current page with one neighbour
 * either side, and a gap wherever pages are skipped. Never more than seven slots, so the
 * bar keeps its width as you page through it rather than growing and shrinking under the
 * pointer.
 *
 *   pageWindow(1, 20)  -> 1 2 3 4 5 … 20
 *   pageWindow(10, 20) -> 1 … 9 10 11 … 20
 *   pageWindow(20, 20) -> 1 … 16 17 18 19 20
 */
export const pageWindow = (currentPage, pageCount) => {
  const count = Math.max(1, pageCount);
  const current = Math.min(Math.max(1, currentPage), count);
  const SLOTS = 7;
  if (count <= SLOTS) return range(1, count);
  // Five numbers at the end that holds the current page, one gap, and the far end.
  if (current <= 4) return [...range(1, 5), PAGE_GAP, count];
  if (current >= count - 3) return [1, PAGE_GAP, ...range(count - 4, count)];
  return [1, PAGE_GAP, current - 1, current, current + 1, PAGE_GAP, count];
};

/**
 * The pager under every paginated table in the CMS.
 *
 * It replaces `shared/itemTable/components/PaginationControl.js`, which was Bootstrap 3's
 * `ul.pagination` strip: `« < 1 2 … 10 ... > »` in bordered boxes, where `...` jumped ten
 * pages ahead, the window slid one page at a time, and nothing said how many rows there
 * were. Collections, the resource lists and the directory screens all drew that strip,
 * each wrapped in its own table footer.
 *
 * Now it is one bar, 56 tall and directly under the last row: how many rows there are on
 * the left, and Previous, the page numbers and Next on the right, 4px apart, drawn with the
 * button primitive. The look is redesign iteration 1 (2026-09-29): Previous and Next are
 * small secondary buttons, greyed out at the ends rather than silently doing nothing; the
 * current page is filled with `--page-current`; the other pages are plain numbers until
 * pointed at.
 *
 * `totalCount` and `pageSize` are optional. Without them there is no "Showing" line, only
 * the controls, and one page with nothing else to show renders nothing, as before. With
 * them the summary stays on a single page too (the redesign's rule: the buttons go, the
 * count stays), so `alwaysShowSummary` no longer changes anything and is kept only so its
 * callers need not change. `leading` and `trailing` are for whatever a table wants in its
 * footer beside the pager.
 *
 * `pageSizeOptions` with `onPageSizeChange` adds a rows-per-page picker, for the tables
 * that always had one (the MUI data grids and the audience preview). It stays on screen at
 * one page, or a table set to show 75 rows could never be set back.
 *
 * `disabled` holds every control while the caller is fetching a page, for the ones that
 * must not start a second fetch before the first lands.
 */
const Pagination = ({
  currentPage = 1,
  pageCount = 1,
  onPageSelected,
  totalCount,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  disabled = false,
  leading,
  trailing,
  className,
}) => {
  const count = Math.max(1, pageCount || 1);
  const page = Math.min(Math.max(1, currentPage || 1), count);
  const hasPages = count > 1;
  const hasSizes = pageSizeOptions?.length > 1 && !!onPageSizeChange;
  const showSummary = totalCount > 0 && pageSize > 0;
  if (!hasPages && !showSummary && !hasSizes && !leading && !trailing) return null;

  const select = (target) => {
    if (disabled) return;
    if (target >= 1 && target <= count && target !== page) onPageSelected?.(target);
  };
  const first = (page - 1) * pageSize + 1;
  const last = Math.min(totalCount, page * pageSize);

  return (
    <nav
      aria-label="Pagination"
      className={cn(
        'cms-pagination tw-flex tw-flex-wrap tw-items-center tw-justify-between',
        'tw-min-h-pager tw-gap-x-6 tw-gap-y-2 tw-py-3 tw-font-sans tw-text-body',
        className
      )}
    >
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-6 tw-gap-y-2">
        {leading}
        {showSummary && (
          <p className="tw-m-0 tw-text-small tw-text-muted tw-tabular-nums">
            Showing{' '}
            <span className="tw-font-semibold tw-text-ink">
              {figures.format(first)}–{figures.format(last)}
            </span>{' '}
            of <span className="tw-font-semibold tw-text-ink">{figures.format(totalCount)}</span>
          </p>
        )}
      </div>
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-6 tw-gap-y-2">
        {hasSizes && (
          <label className="tw-m-0 tw-inline-flex tw-items-center tw-gap-2 tw-font-normal tw-text-small tw-text-muted">
            Rows per page
            <Select
              value={pageSize}
              disabled={disabled}
              onChange={(event) => onPageSizeChange(Number(event.target.value))}
              className="tw-w-auto tw-h-8 tw-py-0 tw-pl-2.5 tw-text-small"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Select>
          </label>
        )}
        {hasPages && (
          <ul className="tw-m-0 tw-flex tw-list-none tw-items-center tw-gap-1 tw-p-0">
            <li>
              <Button
                variant="default"
                size="page"
                aria-label="Previous page"
                disabled={disabled || page === 1}
                className={STEP}
                onClick={() => select(page - 1)}
              >
                <ChevronLeft size={14} aria-hidden="true" />
                Previous
              </Button>
            </li>
            {pageWindow(page, count).map((entry, index) =>
              entry === PAGE_GAP ? (
                <li
                  // Two gaps can be on screen at once, so the key has to say which.
                  key={`gap-${index}`}
                  aria-hidden="true"
                  className="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-text-muted"
                >
                  <Ellipsis size={16} />
                </li>
              ) : (
                <li key={entry}>
                  <Button
                    variant="ghost"
                    size="page"
                    aria-label={`Page ${entry}`}
                    aria-current={entry === page ? 'page' : undefined}
                    disabled={disabled}
                    className={entry === page ? CURRENT : PAGE}
                    onClick={() => select(entry)}
                  >
                    {figures.format(entry)}
                  </Button>
                </li>
              )
            )}
            <li>
              <Button
                variant="default"
                size="page"
                aria-label="Next page"
                disabled={disabled || page === count}
                className={STEP}
                onClick={() => select(page + 1)}
              >
                Next
                <ChevronRight size={14} aria-hidden="true" />
              </Button>
            </li>
          </ul>
        )}
        {trailing}
      </div>
    </nav>
  );
};

export default Pagination;
